import { useMemo } from 'react';
import { Award, Check, Clock, TrendingUp, AlertCircle } from 'lucide-react';

/**
 * useHRMetrics
 * 
 * Calculates onboarding pipeline metrics for HR Hank dashboard
 * Focuses on post-offer stages: Offer Extended through Onboarded
 * 
 * Features:
 * - Onboarding pipeline tiles (4 stages)
 * - Time-based filtering (7d, 30d, 90d, All)
 * - Client and AM filtering
 * - Average onboarding time calculation
 * 
 * @param {Object} params
 * @returns {Object} - All calculated HR metrics
 */
export const useHRMetrics = ({
  submissions = [],
  jobs = [],
  candidates = [],
  users = [],
  selectedTimeframe = 'this_month',
  selectedClients = new Set(),
  selectedAccountManagers = new Set()
}) => {

  // Helper: Check if submission is within timeframe
  const isWithinTimeframe = (submission) => {
    const submissionDate = submission.updated_at 
      ? new Date(submission.updated_at)
      : submission.submission_date 
        ? new Date(submission.submission_date)
        : null;
    
    if (!submissionDate) return false;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    switch (selectedTimeframe) {
      case 'ytd':
        return submissionDate >= new Date(currentYear, 0, 1);
      
      case 'this_month':
        return submissionDate >= new Date(currentYear, currentMonth, 1) &&
               submissionDate < new Date(currentYear, currentMonth + 1, 1);
      
      case 'last_month':
        return submissionDate >= new Date(currentYear, currentMonth - 1, 1) &&
               submissionDate < new Date(currentYear, currentMonth, 1);
      
      case 'this_year':
        return submissionDate >= new Date(currentYear, 0, 1) &&
               submissionDate < new Date(currentYear + 1, 0, 1);
      
      case 'last_year':
        return submissionDate >= new Date(currentYear - 1, 0, 1) &&
               submissionDate < new Date(currentYear, 0, 1);
      
      case 'last_6_months':
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        return submissionDate >= sixMonthsAgo;
      
      case 'last_12_months':
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        return submissionDate >= twelveMonthsAgo;
      
      default:
        return true;
    }
  };

  // Helper: Check if submission matches filters
  const matchesFilters = (submission) => {
    const job = jobs.find(j => j.id === submission.job_id);
    if (!job) return false;

    const client = job.client_company;
    
    // Client filter
    if (selectedClients.size > 0 && !selectedClients.has(client)) {
      return false;
    }

    // Account Manager filter
    if (selectedAccountManagers.size > 0) {
      const jobAM = job.account_manager_id || job.assigned_account_manager;
      if (!selectedAccountManagers.has(jobAM)) {
        return false;
      }
    }

    return true;
  };

  // ============================================================================
  // ONBOARDING PIPELINE METRICS (3 tiles - HR focuses on post-acceptance)
  // ============================================================================
  const onboardingPipelineMetrics = useMemo(() => {
    // Filter submissions by timeframe and filters
    const filteredSubmissions = submissions.filter(sub => 
      isWithinTimeframe(sub) && matchesFilters(sub)
    );

    const metrics = {
      offerAccepted: {
        count: filteredSubmissions.filter(sub => sub.status === 'Offer Accepted').length,
        data: filteredSubmissions.filter(sub => sub.status === 'Offer Accepted'),
        label: 'Offer Accepted',
        description: 'Offer Accepted',
        color: 'blue',
        icon: Clock
      },
      onboardingInitiated: {
        count: filteredSubmissions.filter(sub => 
          sub.status === 'Onboarding Initiated' || sub.status === 'Onboarding'
        ).length,
        data: filteredSubmissions.filter(sub => 
          sub.status === 'Onboarding Initiated' || sub.status === 'Onboarding'
        ),
        label: 'Onboarding Initiated',
        description: 'Onboarding Initiated',
        color: 'orange',
        icon: Clock
      },
      onboarded: {
        count: filteredSubmissions.filter(sub => sub.status === 'Onboarded').length,
        data: filteredSubmissions.filter(sub => sub.status === 'Onboarded'),
        label: 'Onboarded',
        description: 'Onboarded',
        color: 'green',
        icon: Check
      }
    };

    return metrics;

    return metrics;
  }, [submissions, jobs, selectedTimeframe, selectedClients, selectedAccountManagers]);

  // ============================================================================
  // AVERAGE ONBOARDING TIME
  // ============================================================================
  const averageOnboardingTime = useMemo(() => {
    const onboardedSubmissions = submissions.filter(sub => 
      sub.status === 'Onboarded' && 
      isWithinTimeframe(sub) && 
      matchesFilters(sub)
    );

    if (onboardedSubmissions.length === 0) return 0;

    const totalDays = onboardedSubmissions.reduce((acc, sub) => {
      const offerDate = sub.updated_at ? new Date(sub.updated_at) : new Date(sub.submission_date);
      const onboardDate = new Date(); // Would need actual onboard date from data
      const days = Math.floor((onboardDate - offerDate) / (1000 * 60 * 60 * 24));
      return acc + Math.max(0, days);
    }, 0);

    return Math.round(totalDays / onboardedSubmissions.length);
  }, [submissions, selectedTimeframe, selectedClients, selectedAccountManagers]);

  // ============================================================================
  // AT RISK CANDIDATES (Offer Accepted > 7 days ago - needs urgent action)
  // ============================================================================
  const atRiskCandidates = useMemo(() => {
    const offerAccepted = submissions.filter(sub => 
      sub.status === 'Offer Accepted' && 
      matchesFilters(sub)
    );

    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));

    const atRisk = offerAccepted.filter(sub => {
      const offerDate = sub.updated_at ? new Date(sub.updated_at) : new Date(sub.submission_date);
      return offerDate < sevenDaysAgo;
    });

    return {
      count: atRisk.length,
      data: atRisk,
      label: 'Urgent Action Needed',
      description: 'Offer accepted >7 days',
      color: 'red',
      icon: AlertCircle
    };
  }, [submissions, selectedClients, selectedAccountManagers]);

  // ============================================================================
  // ONBOARDING BY CLIENT
  // ============================================================================
  const onboardingByClient = useMemo(() => {
    const clientMetrics = {};

    submissions.forEach(sub => {
      if (!matchesFilters(sub)) return;
      if (!['Offer Accepted', 'Onboarding Initiated', 'Onboarding', 'Onboarded'].includes(sub.status)) return;

      const job = jobs.find(j => j.id === sub.job_id);
      if (!job) return;

      const clientName = job.client_company || 'Unknown';

      if (!clientMetrics[clientName]) {
        clientMetrics[clientName] = {
          name: clientName,
          offerAccepted: 0,
          onboardingInitiated: 0,
          onboarded: 0,
          total: 0
        };
      }

      clientMetrics[clientName].total++;
      if (sub.status === 'Offer Accepted') clientMetrics[clientName].offerAccepted++;
      if (sub.status === 'Onboarding Initiated' || sub.status === 'Onboarding') clientMetrics[clientName].onboardingInitiated++;
      if (sub.status === 'Onboarded') clientMetrics[clientName].onboarded++;
    });

    return Object.values(clientMetrics).sort((a, b) => b.total - a.total);
  }, [submissions, jobs, selectedClients, selectedAccountManagers]);

  // ============================================================================
  // ONBOARDING BY ACCOUNT MANAGER
  // ============================================================================
  const onboardingByAM = useMemo(() => {
    const amMetrics = {};

    submissions.forEach(sub => {
      if (!matchesFilters(sub)) return;
      if (!['Offer Accepted', 'Onboarding Initiated', 'Onboarding', 'Onboarded'].includes(sub.status)) return;

      const job = jobs.find(j => j.id === sub.job_id);
      if (!job) return;

      const amId = job.account_manager_id || job.assigned_account_manager || 'unassigned';
      const am = users.find(u => u.id === amId);
      const amName = am ? (am.full_name || am.username) : 'Unassigned';

      if (!amMetrics[amId]) {
        amMetrics[amId] = {
          id: amId,
          name: amName,
          offerAccepted: 0,
          onboardingInitiated: 0,
          onboarded: 0,
          total: 0
        };
      }

      amMetrics[amId].total++;
      if (sub.status === 'Offer Accepted') amMetrics[amId].offerAccepted++;
      if (sub.status === 'Onboarding Initiated' || sub.status === 'Onboarding') amMetrics[amId].onboardingInitiated++;
      if (sub.status === 'Onboarded') amMetrics[amId].onboarded++;
    });

    return Object.values(amMetrics).sort((a, b) => b.total - a.total);
  }, [submissions, jobs, users, selectedClients, selectedAccountManagers]);

  return {
    onboardingPipelineMetrics,
    atRiskCandidates,
    onboardingByClient,
    onboardingByAM
  };
};

export default useHRMetrics;
